import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Dropdown, Checkbox, Message } from 'semantic-ui-react';
import { useComponentManager } from '../../providers/ComponentManagerProvider';

const NOT_CHOSEN = '';

const TYPE_ORDER = [
    'Building',
    'Space',
    'Equipment',
    'Component',
    'Sub Component',
    'Enterprise',
    'Document',
];

const typeRank = name => {
    const index = TYPE_ORDER.indexOf(name);
    return index === -1 ? TYPE_ORDER.length : index;
};

const formStyle = {
    field: {
        display: 'flex',
        alignItems: 'center',
    },
    label: {
        fontSize: '1.2rem',
        marginRight: '1rem',
        width: '40%',
    },
    input: {
        width: '100%',
    },
};

export const ComponentModal = ({
    open,
    setOpen,
    mode = 'create',
    parentId = null,
    componentId = null,
    onCreated,
    onUpdated,
    onMissing,
}) => {
    const { createComponent, updateComponentDetails, getComponentDetails, getComponentTypes } =
        useComponentManager();

    const isEdit = mode === 'edit';

    const [num, setNum] = useState('');
    const [description, setDescription] = useState('');
    const [haystackTag, setHaystackTag] = useState('');
    const [typeId, setTypeId] = useState(NOT_CHOSEN);
    const [selectable, setSelectable] = useState(false);
    const [variableQuantity, setVariableQuantity] = useState(false);
    const [group, setGroup] = useState(false);

    const [types, setTypes] = useState([]);
    const [typesError, setTypesError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState([]);

    const isRoot = parentId == null;

    const clearForm = () => {
        setNum('');
        setDescription('');
        setHaystackTag('');
        setTypeId(NOT_CHOSEN);
        setSelectable(false);
        setVariableQuantity(false);
        setGroup(false);
        setErrors([]);
    };

    const populate = detail => {
        setNum(detail?.num ?? '');
        setDescription(detail?.description ?? '');
        setHaystackTag(detail?.haystack_tag ?? '');
        setTypeId(detail?.component_type_id ?? NOT_CHOSEN);
        setSelectable(!!detail?.selectable);
        setVariableQuantity(!!detail?.variable_quantity);
        setGroup(!!detail?.group);
    };

    useEffect(() => {
        if (!open) return;
        clearForm();
        setTypesError(false);
        setLoadError(false);
        getComponentTypes()
            .then(res => setTypes(res?.data ?? []))
            .catch(() => setTypesError(true));

        if (isEdit && componentId) {
            setIsLoading(true);
            getComponentDetails(componentId)
                .then(res => {
                    populate(res?.data ?? null);
                    setIsLoading(false);
                })
                .catch(err => {
                    setIsLoading(false);
                    if (err?.response?.status === 404) {
                        setOpen(false);
                        if (onMissing) onMissing();
                    } else {
                        setLoadError(true);
                    }
                });
        }
    }, [open, isEdit, componentId, getComponentTypes, getComponentDetails]);

    const orderedTypes = [...types].sort((a, b) => typeRank(a.name) - typeRank(b.name));

    const typeOptions = [
        { key: 'not-chosen', text: 'Not chosen', value: NOT_CHOSEN },
        ...orderedTypes.map(t => ({ key: t.id, text: t.name, value: t.id })),
    ];

    const handleSubmit = async () => {
        if (!num.trim()) {
            setErrors(['A component number is required.']);
            return;
        }
        if (num.trim().length > 50) {
            setErrors(['The component number must be 50 characters or fewer.']);
            return;
        }
        if (!description.trim()) {
            setErrors(['A component description is required.']);
            return;
        }
        if (description.trim().length > 100) {
            setErrors(['The description must be 100 characters or fewer.']);
            return;
        }

        setIsSubmitting(true);
        setErrors([]);

        try {
            const fields = {
                num: num.trim(),
                description: description.trim(),
                haystackTag: haystackTag.trim(),
                componentTypeId: typeId === NOT_CHOSEN ? null : typeId,
                selectable,
                variableQuantity,
                group,
            };

            if (isEdit) {
                const res = await updateComponentDetails({ id: componentId, ...fields });
                await onUpdated(res?.data);
            } else {
                const res = await createComponent({ parentId, ...fields });
                await onCreated(res?.data);
            }
            setOpen(false);
        } catch (error) {
            if (isEdit && error?.response?.status === 404) {
                setOpen(false);
                if (onMissing) onMissing();
                return;
            }
            const message =
                error?.response?.data?.message ||
                error?.response?.data ||
                (isEdit
                    ? 'Failed to update component. Please try again.'
                    : 'Failed to create component. Please try again.');
            setErrors([message]);
        } finally {
            setIsSubmitting(false);
        }
    };

    const header = isEdit
        ? 'Edit Component'
        : isRoot
        ? 'Add Root Component'
        : 'Add Child Component';

    const busy = isSubmitting || isLoading;

    return (
        <Modal
            size="tiny"
            closeIcon={!busy}
            open={open}
            onClose={() => {
                if (busy) return;
                setOpen(false);
            }}
            style={{ textAlign: 'left' }}
        >
            <Modal.Header>{header}</Modal.Header>
            <Modal.Content>
                {loadError ? (
                    <Message negative>
                        The component couldn't be loaded. Please close this dialog and try again.
                    </Message>
                ) : (
                    <Form error={errors.length > 0} loading={busy}>
                        <Form.Field required={!isEdit} style={formStyle.field}>
                            <label style={formStyle.label}>Number</label>
                            {isEdit ? (
                                <span style={formStyle.input}>{num}</span>
                            ) : (
                                <input
                                    placeholder="Number"
                                    style={formStyle.input}
                                    value={num}
                                    maxLength={50}
                                    onChange={e => setNum(e.target.value)}
                                    disabled={busy}
                                />
                            )}
                        </Form.Field>
                        <Form.Field required style={formStyle.field}>
                            <label style={formStyle.label}>Description</label>
                            <input
                                placeholder="Description"
                                style={formStyle.input}
                                value={description}
                                maxLength={100}
                                onChange={e => setDescription(e.target.value)}
                                disabled={busy}
                            />
                        </Form.Field>
                        <Form.Field style={formStyle.field}>
                            <label style={formStyle.label}>Haystack Tag</label>
                            <input
                                placeholder="Haystack Tag"
                                style={formStyle.input}
                                value={haystackTag}
                                onChange={e => setHaystackTag(e.target.value)}
                                disabled={busy}
                            />
                        </Form.Field>
                        <Form.Field style={formStyle.field}>
                            <label style={formStyle.label}>Type</label>
                            <Dropdown
                                placeholder="Type"
                                options={typeOptions}
                                value={typeId}
                                onChange={(e, { value }) => setTypeId(value)}
                                disabled={busy || typesError}
                                selection
                                style={formStyle.input}
                            />
                        </Form.Field>
                        {typesError && (
                            <Message negative size="tiny">
                                Component types couldn't be loaded. You can still save without a type.
                            </Message>
                        )}
                        <Form.Field>
                            <Checkbox
                                label="Selectable"
                                checked={selectable}
                                onChange={(e, { checked }) => setSelectable(checked)}
                                disabled={busy}
                            />
                        </Form.Field>
                        <Form.Field>
                            <Checkbox
                                label="Variable Quantity"
                                checked={variableQuantity}
                                onChange={(e, { checked }) => setVariableQuantity(checked)}
                                disabled={busy}
                            />
                        </Form.Field>
                        <Form.Field>
                            <Checkbox
                                label="Group"
                                checked={group}
                                onChange={(e, { checked }) => setGroup(checked)}
                                disabled={busy}
                            />
                        </Form.Field>
                        <div className="error-container">
                            <Message error list={errors} />
                        </div>
                    </Form>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={() => setOpen(false)} disabled={busy}>
                    Cancel
                </Button>
                <Button
                    className="ui primary button"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={busy || loadError}
                >
                    {isEdit ? 'Save' : 'Submit'}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};
